import React, { Fragment, useState, useEffect, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  getTranslations,
  setLanguage,
  getLanguage,
} from "../../services/languageUtils";

import AccountManagerService from "../../services/Celcoin/accountManagerService";
import UtilService from "../../services/Celcoin/utilService";
import { isCripto } from "../../utils/isCripto";
import {
  hasBaasEnabled,
  hasRampsEnabled,
  hasSwapEnabled,
} from "../../utils/servicesEnable";

function SideMenu() {
  const location = window.location;
  const pathname = location.pathname;
  const [translations, setTranslations] = useState({});
  const [language, setLang] = useState(getLanguage());
  const [showMenu, setShowMenu] = useState(false);
  const [valueBalance, setValueBalance] = useState("-.--");
  const [viewBalance, setViewBalance] = useState(false);
  const [hovered, setHovered] = useState("");
  const [mobileExpanded, setMobileExpanded] = useState({
    transfers: false,
    accounts: false,
    settings: false,
  });
  const [colors, setColors] = useState({
    primary: "#1c787e",
    secondary: "#061B2B",
  });
  const [higherDomain, setDomain] = useState(true);

  const selectColors = (theme) => {
    const themes = {
      zicredit: {
        primary: "#FFCC00",
        secondary: "#061B2B",
      },
      default: {
        primary: "#1c787e",
        secondary: "#061B2B",
      },
    };
    return themes[theme] || themes.default;
  };

  const verifyDomain = (domain) => {
    const hostname = window.location.hostname;
    return hostname.includes(domain);
  };

  useEffect(() => {
    if (verifyDomain("zicredit.com")) {
      setColors(selectColors("zicredit"));
      setDomain(false);
    } else {
      setColors(selectColors("default"));
      setDomain(true);
    }

    const loadTranslations = async () => {
      const trans = await getTranslations(language);
      setTranslations(trans);
    };
    loadTranslations();
    loadBalance();
  }, [language]);

  const emailUser = (sessionStorage.getItem("emailUser") || "").toLowerCase();
  const nameUser = sessionStorage.getItem("nameUser") || "";
  const fantasyName = sessionStorage.getItem("fantasyName") || "";
  const baasEnabled = hasBaasEnabled();
  const rampsEnabled = hasRampsEnabled();
  const swapEnabled = hasSwapEnabled();

  const logout = () => {
    UtilService.logout();
  };

  const goTo = (url) => {
    window.location.href = url;
    setShowMenu(false);
  };

  const loadBalance = () => {
    AccountManagerService.showBalance()
      .then((res) => {
        setValueBalance(res.data.body.amount);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  function getLetter(name) {
    if (!name || name.length < 2) {
      return "--";
    }
    let letter = "" + name[0];
    if (name.indexOf(" ") > 0) {
      for (let i = 0; i < name.length; i++) {
        if (name[i] === " " && name.length > i + 1) {
          letter = letter + name[i + 1];
          return letter.toUpperCase();
        }
      }
    }
    letter = letter + name[1];
    return letter.toUpperCase();
  }

  function getName(name) {
    try {
      var tmp = name.split(" ");
      return tmp.length > 1 ? tmp[0] + " " + tmp[tmp.length - 1] : tmp[0];
    } catch (err) {
      return "";
    }
  }

  function formatToBRL(number) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(number);
  }

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setLang(lang);
  };

  const logoSource = useMemo(() => {
    const logoRules = [
      {
        match: (email) => email.includes("contato@zicredit.com.br"),
        src: require("../../assets/imgs/logozicredit.jpeg"),
      },
      {
        match: (email) =>
          email.includes("default@eroninternational.com") ||
          email.includes("administrativo@renovapaypagamentos.com") ||
          email.includes("victor.nery@firebanking.io"),
        src: require("../../assets/imgs/higherLogoGreen.png"),
      },
      {
        match: (email) => email.includes("omena.davyson@primepag.com.br"),
        src: require("../../assets/imgs/primepag.jpeg"),
      },
      {
        match: (email) => email.includes("@pmi.tech"),
        src: require("../../assets/imgs/PMI.png"),
      },
      {
        match: (email) => email.includes("@tcr.financ"),
        src: require("../../assets/imgs/tcr.jpeg"),
      },
      {
        match: (email) => email.includes("@comprarbitcoin.trade"),
        src: require("../../assets/imgs/cb.jpeg"),
      },
      {
        match: (email) => email.includes("viniciusdigitalempresas@gmail.com"),
        src: require("../../assets/imgs/88.jpeg"),
      },
      {
        match: (email) => email.includes("athan.diogo@cipay.com.br"),
        src: require("../../assets/imgs/cipay.png"),
      },
    ];

    const found = logoRules.find((rule) => rule.match(emailUser));
    return found?.src || require("../../assets/imgs/higher-logo-v2.png");
  }, [emailUser]);

  const menuConfig = useMemo(
    () => [
      {
        key: "home",
        label: translations.home,
        iconActive: require("../../assets/imgs/ico-home.svg").default,
        iconDefault: require("../../assets/imgs/ic-home.svg").default,
        visible: baasEnabled,
        active: pathname === "/user",
        onClick: () => goTo("/user"),
      },
      {
        key: "transfers",
        label: translations.transfers,
        iconActive: require("../../assets/imgs/ic-transfer-act.svg").default,
        iconDefault: require("../../assets/imgs/ico-transfer-act.png"),
        visible: baasEnabled,
        active: pathname.includes("/transfers"),
        onClick: () => goTo("/transfers/pix"),
        subitems: [
          {
            key: "pix",
            label: translations.pix,
            active: pathname === "/transfers/pix",
            onClick: () => goTo("/transfers/pix"),
          },
          /*
            TED bancário pela ordem de payout. Entra no menu porque sem link a tela existe e
            ninguém chega nela — foi o que aconteceu com a aba de swap da mesa.
          */
          {
            key: "tedBank",
            label: "TED",
            active: pathname === "/transfers/tedBank",
            onClick: () => goTo("/transfers/tedBank"),
          },
        ],
      },
      {
        key: "accounts",
        label: translations.account,
        iconActive: require("../../assets/imgs/ico-account.svg").default,
        iconDefault: require("../../assets/imgs/ico-account.svg").default,
        visible: baasEnabled,
        active:
          pathname === "/accounts/statements" ||
          pathname === "/accounts/payin" ||
          pathname === "/accounts/payout" ||
          pathname === "/accounts/med",
        onClick: () => goTo("/accounts/statements"),
        subitems: [
          {
            key: "statement",
            label: translations.statement,
            active: pathname === "/accounts/statements",
            onClick: () => goTo("/accounts/statements"),
          },
          {
            key: "payin",
            label: "Payin",
            active: pathname === "/accounts/payin",
            onClick: () => goTo("/accounts/payin"),
          },
          {
            key: "payout",
            label: "Payout",
            active: pathname === "/accounts/payout",
            onClick: () => goTo("/accounts/payout"),
          },
          /*
            Chaves PIX NÃO entram aqui: já existem na aba "Chaves" da tela de PIX, e duas
            entradas para a mesma coisa dividem a atenção sem dividir o trabalho.
          */
          /*
            MED no menu, e não escondido: um caso aberto CONTRA a conta tem prazo, e a tela só
            serve se o cliente souber que ela existe.
          */
          {
            key: "med",
            label: "MED",
            active: pathname === "/accounts/med",
            onClick: () => goTo("/accounts/med"),
          },
        ],
      },
      {
        key: "crypto",
        label: "Crypto",
        iconActive: require("../../assets/imgs/ico-account.svg").default,
        iconDefault: require("../../assets/imgs/ico-account.svg").default,
        visible: isCripto(),
        active:
          pathname === "/cryptocurrency" ||
          pathname.startsWith("/cryptocurrency/"),
        onClick: () => goTo("/cryptocurrency"),
      },
      {
        key: "ramps",
        label: "Ramps",
        iconActive: require("../../assets/imgs/ico-account.svg").default,
        iconDefault: require("../../assets/imgs/ico-account.svg").default,
        visible: rampsEnabled,
        active: pathname === "/ramps",
        onClick: () => goTo("/ramps"),
      },
      {
        key: "swap",
        label: "Swap",
        iconActive: require("../../assets/imgs/ico-exchange.svg").default,
        iconDefault: require("../../assets/imgs/ico-exchange.svg").default,
        visible: swapEnabled,
        active: pathname === "/swap",
        onClick: () => goTo("/swap"),
      },
      {
        key: "settings",
        label: translations.settings,
        iconActive: require("../../assets/imgs/ico-settings.svg").default,
        iconDefault: require("../../assets/imgs/ic-settings.svg").default,
        visible: true,
        active:
          pathname === "/settingsAndSecurity" ||
          pathname.startsWith("/settingsAndSecurity/") ||
          pathname === "/contabil",
        onClick: () => goTo("/settingsAndSecurity"),
        // O extrato contábil (/contabil) saiu do menu: não é mais usado. A rota
        // continua existindo, só não é oferecida.
        subitems: [],
      },
    ],
    [
      translations.home,
      translations.transfers,
      translations.pix,
      translations.account,
      translations.statement,
      translations.settings,
      pathname,
      baasEnabled,
      rampsEnabled,
      swapEnabled,
    ],
  );

  const visibleMenus = menuConfig.filter((item) => item.visible);

  const shouldOpenDesktopSubmenu = (item) => {
    if (!item.subitems?.length) return false;
    return item.active || hovered === item.key;
  };

  const toggleMobileSection = (key) => {
    setMobileExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <section className="sideMenuComponent" id="sideMenuComponent">
      <div className="desktop">
        <div
          style={{
            display: "flex",
            minHeight: "100%",
            flexDirection: "column",
          }}
        >
          <div className="logoLeft" style={{ margin: "0px 0px 10px 13px" }}>
            <img
              src={logoSource}
              style={{ height: "60px", cursor: "pointer" }}
              alt="Ícone Higher"
              className="iconHigherImage"
              onClick={() => goTo("/user")}
            />
          </div>
          <div
            className="contentName"
            style={{ borderBottom: "1px solid #EBEBED", marginLeft: "10px" }}
          >
            <div className="contentLetter">{getLetter(nameUser)}</div>

            <div className="contentCardData">
              <div className="title">
                <p style={{ fontWeight: "bold", margin: "-7px 0px 5px 0px" }}>
                  {getName(nameUser)}
                </p>
                {!emailUser.includes("@pmi.tech") && (
                  <p className="subtitle" style={{ fontWeight: "normal" }}>
                    {fantasyName}
                  </p>
                )}
              </div>
            </div>

            {baasEnabled && (
              <div style={{ marginLeft: "3px" }}>
                <span style={{ color: "#696C70" }}>
                  {translations.currentBalance}
                </span>
                <br />
                <b>
                  {viewBalance ? formatToBRL(valueBalance) : "**********"}{" "}
                  &nbsp;&nbsp;&nbsp;&nbsp;
                </b>
                {viewBalance && (
                  <img
                    src={require("../../assets/imgs/open-eye.svg").default}
                    style={{ display: "inline-block", cursor: "pointer" }}
                    alt="Icone visualizar saldo"
                    onClick={() => {
                      setViewBalance(false);
                    }}
                  />
                )}
                {!viewBalance && (
                  <img
                    src={require("../../assets/imgs/close-eye.svg").default}
                    style={{
                      border: "3px solid rgba(0,0,0,0)",
                      display: "inline-block",
                      cursor: "pointer",
                    }}
                    alt="Icone visualizar saldo"
                    onClick={async () => {
                      await loadBalance();
                      setViewBalance(true);
                    }}
                  />
                )}
              </div>
            )}

            <br />
          </div>

          {visibleMenus.map((item, index) => (
            <div
              key={item.key}
              className="btnSimple"
              style={
                item.active
                  ? {
                      color: colors.primary,
                      fontWeight: "bold",
                      ...(index === 0 ? { marginTop: "40px" } : {}),
                    }
                  : index === 0
                    ? { marginTop: "40px" }
                    : {}
              }
              onMouseOver={() => setHovered(item.key)}
              onMouseOut={() => setHovered("")}
            >
              {higherDomain && (
                <img
                  src={
                    item.active || hovered === item.key
                      ? item.iconActive
                      : item.iconDefault
                  }
                  alt="Ícone menu lateral"
                  className="iconMenu"
                />
              )}
              <b onClick={item.onClick}>{item.label}</b>
              {shouldOpenDesktopSubmenu(item) && (
                <span>
                  {item.subitems.map((subitem) => (
                    <div
                      key={subitem.key}
                      onClick={subitem.onClick}
                      className="subitemMenu"
                      style={
                        subitem.active
                          ? { color: colors.primary, fontWeight: "normal" }
                          : { fontWeight: "normal" }
                      }
                    >
                      {subitem.label}
                    </div>
                  ))}
                </span>
              )}
            </div>
          ))}

          <br />
          <br />
          <br />

          <div
            className="languageSelector"
            style={{
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
            }}
          >
            <button
              onClick={() => handleLanguageChange("en")}
              disabled={language === "en"}
              style={{ margin: "10px" }}
            >
              <img src={require("../../assets/imgs/USD.png")} alt="English" />
            </button>
            <button
              onClick={() => handleLanguageChange("pt")}
              disabled={language === "pt"}
              style={{ margin: "10px" }}
            >
              <img src={require("../../assets/imgs/BRL.png")} alt="Português" />
            </button>
          </div>

          <div
            style={{
              position: "relative",
              width: "100%",
              paddingLeft: "15px",
              marginTop: "5px",
            }}
          >
            <div
              onClick={() => {
                setShowMenu(false);
                logout();
              }}
              className="btnSimple"
              style={{ fontWeight: "bolder" }}
            >
              {translations.signOut}&nbsp;&nbsp;&nbsp;
            </div>
          </div>
        </div>
      </div>

      <span className="mobile">
        <div>
          <div className="contentFakeHeader"></div>
          <div className="contentHeader">
            <div
              className="container"
              onClick={(e) => {
                e.preventDefault();
                setShowMenu(!showMenu);
              }}
            >
              <input type="checkbox" id="checkbox-menu" />

              <label htmlFor="checkbox-menu">
                <span></span>
                <span></span>
                <span></span>
              </label>
            </div>

            <div className="contentIconKeyInput">
              <img
                src={
                  emailUser.includes("contato@zicredit.com.br")
                    ? require("../../assets/imgs/logozicredit.jpeg")
                    : require("../../assets/imgs/ico-higher-bg.svg").default
                }
                alt="Ícone Higher"
                style={{
                  cursor: "pointer",
                  ...(emailUser.includes("contato@zicredit.com.br")
                    ? {
                        width: "35px",
                        height: "35px",
                        top: "17px",
                        position: "relative",
                      }
                    : {}),
                }}
                className={
                  emailUser.includes("contato@zicredit.com.br")
                    ? "iconHigherImage"
                    : "iconKeyInput"
                }
                onClick={() => goTo("/user")}
              />
              <div className="contentLetter">{getLetter(nameUser)}</div>
            </div>
          </div>

          <Transition.Root
            show={showMenu}
            as={Fragment}
            style={{ zIndex: "1000" }}
          >
            <Dialog as="div" className="relative z-10" onClose={setShowMenu}>
              <div className="fixed inset-0" />

              <div className="fixed inset-0 overflow-hidden">
                <div className="absolute inset-0 overflow-hidden">
                  <div className="pointer-events-none fixed inset-y-0 left-0 flex max-w-full pl-0 sm:pl-0">
                    <Transition.Child
                      as={Fragment}
                      enter="transform transition ease-in-out duration-500 sm:duration-700"
                      enterFrom="-translate-x-full"
                      enterTo="-translate-x-0"
                      leave="transform transition ease-in-out duration-500 sm:duration-700"
                      leaveFrom="-translate-x-0"
                      leaveTo="-translate-x-full"
                    >
                      <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                        <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                          <div className="p-6">
                            <div className="flex items-start justify-between">
                              <button
                                type="button"
                                className="relative rounded-md bg-white focus:ring-2 focus:ring-indigo-500"
                                style={{ color: colors.primary }}
                                onClick={() => setShowMenu(false)}
                              >
                                <span className="absolute -inset-2.5" />
                                <span className="sr-only">Close panel</span>
                                <XMarkIcon
                                  className="h-6 w-6"
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                          </div>
                          <ul className="flex-1 divide-y divide-gray-200 overflow-y-auto">
                            <div
                              className="languageSelector"
                              style={{
                                display: "flex",
                                justifyContent: "space-around",
                                alignItems: "center",
                              }}
                            >
                              <button
                                onClick={() => handleLanguageChange("en")}
                                disabled={language === "en"}
                                style={{ margin: "10px" }}
                              >
                                <img
                                  src={require("../../assets/imgs/USD.png")}
                                  alt="English"
                                />
                              </button>
                              <button
                                onClick={() => handleLanguageChange("pt")}
                                disabled={language === "pt"}
                                style={{ margin: "10px" }}
                              >
                                <img
                                  src={require("../../assets/imgs/BRL.png")}
                                  alt="Português"
                                />
                              </button>
                            </div>

                            {visibleMenus.map((item) => (
                              <Fragment key={`mobile-${item.key}`}>
                                <li>
                                  <div className="group relative flex items-center px-5 py-6">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (item.subitems?.length) {
                                          toggleMobileSection(item.key);
                                          return;
                                        }
                                        item.onClick();
                                      }}
                                      className="-m-1 block flex-1 p-1"
                                    >
                                      <div
                                        className="absolute inset-0 group-hover:bg-gray-50"
                                        aria-hidden="true"
                                      />
                                      <div className="relative flex min-w-0 flex-1 items-center">
                                        <span className="relative inline-block flex-shrink-0">
                                          <img
                                            className="h-6 w-6"
                                            src={
                                              item.active
                                                ? item.iconActive
                                                : item.iconDefault
                                            }
                                            alt=""
                                          />
                                        </span>
                                        <div className="ml-4 truncate">
                                          <p className="truncate text-lg font-medium font-bold text-gray-900">
                                            {item.label}
                                          </p>
                                        </div>
                                      </div>
                                    </button>
                                  </div>
                                </li>

                                {item.subitems?.length > 0 &&
                                  mobileExpanded[item.key] && (
                                    <span>
                                      {item.subitems.map((subitem) => (
                                        <li key={`mobile-sub-${subitem.key}`}>
                                          <div className="group relative flex items-center px-5 py-6 bg-gray-100">
                                            <button
                                              type="button"
                                              onClick={subitem.onClick}
                                              className="-m-1 block flex-1 p-1 text-left"
                                            >
                                              <div
                                                className="absolute inset-0"
                                                aria-hidden="true"
                                              />
                                              <div className="relative flex min-w-0 flex-1 items-center">
                                                <span className="relative inline-block flex-shrink-0"></span>
                                                <div className="ml-4 truncate">
                                                  <p className="truncate text-lg font-medium text-gray-900">
                                                    {subitem.label}
                                                  </p>
                                                </div>
                                              </div>
                                            </button>
                                          </div>
                                        </li>
                                      ))}
                                    </span>
                                  )}
                              </Fragment>
                            ))}

                            <li>
                              <div className="group relative flex items-center px-5 py-6">
                                <button
                                  type="button"
                                  onClick={logout}
                                  className="-m-1 block flex-1 p-1 text-left"
                                >
                                  <div
                                    className="absolute inset-0 group-hover:bg-gray-50"
                                    aria-hidden="true"
                                  />
                                  <div className="relative flex min-w-0 flex-1 items-center">
                                    <div className="ml-4 truncate">
                                      <p className="truncate text-lg font-medium font-bold text-gray-900">
                                        {translations.signOut}
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              </div>
                            </li>
                          </ul>
                        </div>
                      </Dialog.Panel>
                    </Transition.Child>
                  </div>
                </div>
              </div>
            </Dialog>
          </Transition.Root>
        </div>
      </span>
    </section>
  );
}

export default SideMenu;
